import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { MenuItem } from "primeng/api";
import { AdminPageContainerComponent } from "src/app/components/ape-admin-page-container/ape-admin-page-container.component";
import { ApeButtonComponent } from "src/app/components/ape-button/ape-button.component";
import { ApeDownloadButtonComponent } from "src/app/components/ape-download-button/ape-download-button.component";
import { ApeInputComponent } from "src/app/components/ape-input/ape-input.component";
import { ApePagePlaceholderComponent } from "src/app/components/ape-page-placeholder/ape-page-placeholder.component";
import { ApeProcessSetupSkeletonComponent } from "src/app/components/ape-process-setup-skeleton/ape-process-setup-skeleton.component";
import { ApeSelectComponent } from "src/app/components/ape-select/ape-select.component";
import { ApeStatusBadgeComponent } from "src/app/components/ape-status-badge/ape-status-badge.component";
import { ApeTablePaginationComponent } from "src/app/components/ape-table-pagination/ape-table-pagination.component";
import { ApeAdminMonitoringGoalSettingUsersTableComponent } from "src/app/components/ape-admin-monitoring-goal-setting-users-table/ape-admin-monitoring-goal-setting-users-table.component";
import { ApeMonitoringGoalSettingDetailsFilterDrawerComponent } from "src/app/components/ape-monitoring-goal-setting-details-filter-drawer/ape-monitoring-goal-setting-details-filter-drawer.component";
import { MonitoringGoalSettingDetailsCountersTemplateComponent } from "../monitoring-goal-setting-details-counters-template/monitoring-goal-setting-details-counters-template.component";
import { TranslatePipe } from "src/app/pipe/translation.filter.pipe";
import { TRANSLATION_KEYS } from "src/app/constants/translations/translation-keys";
import { StatusCardData } from "src/app/components/ape-status-card/ape-status-card.component";
import { MonitoringGoalSettingUserFiltersValues, MonitoringGoalSettingUserSummary } from "alloy-performance-engine-cl";
import { UserMenuItem } from "src/app/constants/utils-type";

export type GoalSettingAppliedChipType =
  | "APPLIED_CARD_STATUSES"
  | "APPLIED_PROPOSAL_STATUSES"
  | "APPLIED_UPDATES"
  | "APPLIED_SCORECARD_TYPES"
  | "APPLIED_MANAGERS"
  | "APPLIED_DEPARTMENTS";

export interface GoalSettingAppliedChip {
  id: string;
  label: string;
  type: GoalSettingAppliedChipType;
}

/**
 * Template di presentazione per la pagina di monitoraggio Goal Setting.
 * Replica struttura e stile dei template di monitoraggio Evaluation/DevelopmentPlan:
 * container con titolo+back, KPI in testa, toolbar (search + filtri + sort), chip
 * filtri applicati, contatore filtri/risultati, tabella restylata, paginazione,
 * placeholder/skeleton, drawer filtri.
 */
@Component({
  selector: "process-monitoring-goal-setting-details-template",
  standalone: true,
  host: { class: "w-full h-full" },
  templateUrl: "./process-monitoring-goal-setting-details-template.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslatePipe,
    AdminPageContainerComponent,
    MonitoringGoalSettingDetailsCountersTemplateComponent,
    ApeAdminMonitoringGoalSettingUsersTableComponent,
    ApeMonitoringGoalSettingDetailsFilterDrawerComponent,
    ApeButtonComponent,
    ApeDownloadButtonComponent,
    ApeInputComponent,
    ApeSelectComponent,
    ApeStatusBadgeComponent,
    ApeTablePaginationComponent,
    ApePagePlaceholderComponent,
    ApeProcessSetupSkeletonComponent
  ]
})
export class ProcessMonitoringGoalSettingDetailsTemplateComponent {
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  @Input() loading: boolean = true;

  @Input() loadingUsers: boolean = false;

  @Input() loadingUsersCount: boolean = false;

  @Input() processName: string = "";

  @Input() countersCardData: StatusCardData[] = [];

  @Input() usersData: MonitoringGoalSettingUserSummary[] = [];

  @Input() usersDataCount: number = 0;

  @Input() totalPages: number = 0;

  @Input() searchedText: string = "";

  @Input() page: number = 1;

  @Input() pageSize: number = 10;

  @Input() sortOptions: MenuItem[] = [];

  @Input() selectedSortOption: MenuItem | undefined = undefined;

  @Input() drawerVisible: boolean = false;

  @Input() activeFiltersCount: number = 0;

  @Input() appliedChips: GoalSettingAppliedChip[] = [];

  // Drawer: opzioni e selezioni in-flight
  @Input() cardStatusOptions: MenuItem[] = [];

  @Input() proposalStatusOptions: MenuItem[] = [];

  @Input() updatesOptions: MenuItem[] = [];

  @Input() scorecardTypeOptions: MenuItem[] = [];

  @Input() managerOptions: UserMenuItem[] = [];

  @Input() departmentOptions: MenuItem[] = [];

  @Input() selectedCardStatuses: MenuItem[] = [];

  @Input() selectedProposalStatuses: MenuItem[] = [];

  @Input() selectedUpdates: MenuItem[] = [];

  @Input() selectedScorecardTypes: MenuItem[] = [];

  @Input() selectedManagers: UserMenuItem[] = [];

  @Input() selectedDepartments: MenuItem[] = [];

  @Input() filtersValues: MonitoringGoalSettingUserFiltersValues | null = null;

  @Output() backClick = new EventEmitter<void>();

  @Output() searchChange = new EventEmitter<string>();

  @Output() searchUsers = new EventEmitter<void>();

  @Output() sortChange = new EventEmitter<MenuItem>();

  @Output() pageChange = new EventEmitter<number>();

  @Output() pageSizeChange = new EventEmitter<number>();

  @Output() advancedFiltersClicked = new EventEmitter<void>();

  @Output() solicitDefinitionsClicked = new EventEmitter<void>();

  @Output() solicitProposalsClicked = new EventEmitter<void>();

  @Output() exportXlsClicked = new EventEmitter<string>();

  @Output() removeFilterChip = new EventEmitter<GoalSettingAppliedChip>();

  @Output() cardStatusesChange = new EventEmitter<MenuItem[]>();

  @Output() proposalStatusesChange = new EventEmitter<MenuItem[]>();

  @Output() updatesChange = new EventEmitter<MenuItem[]>();

  @Output() scorecardTypesChange = new EventEmitter<MenuItem[]>();

  @Output() managersChange = new EventEmitter<UserMenuItem[]>();

  @Output() departmentsChange = new EventEmitter<MenuItem[]>();

  @Output() applyFilters = new EventEmitter<void>();

  @Output() resetFilters = new EventEmitter<void>();

  @Output() closeDrawer = new EventEmitter<void>();

  @Output() impersonateUser = new EventEmitter<MonitoringGoalSettingUserSummary>();

  @Output() impersonateManager = new EventEmitter<MonitoringGoalSettingUserSummary>();

  @Output() downloadPdf = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M4 al manager del collaboratore (singolo). */
  @Output() solicitManager = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M3 al collaboratore per le proprie proposte (singolo). */
  @Output() solicitUser = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito M6 al manager: ricondivisione scheda in fase di Aggiornamento (singolo). */
  @Output() solicitManagerUpdate = new EventEmitter<MonitoringGoalSettingUserSummary>();

  /** Sollecito massivo M6 (Reminder aggiornamento manager). */
  @Output() solicitUpdatesClicked = new EventEmitter<void>();
  // NB: i pulsanti bulk M3/M4 esistevano già nella toolbar (SOLICIT_DEFINITIONS / SOLICIT_PROPOSALS)
  // e usano gli Output `solicitDefinitionsClicked` / `solicitProposalsClicked` definiti più sopra
  // in questo file. Non aggiungiamo nuovi output per evitare duplicazioni.

  trackChip(_: number, chip: GoalSettingAppliedChip): string {
    return chip.id + "_" + chip.type;
  }
}
